import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { getMapSpots } from '../../../api/spaceApi';

// All dots are the same fixed size so both mini-maps have identical dimensions.
// The highlight is shown via fill colour + box-shadow ring (shadow doesn't affect layout).
function Dot({ spot, highlightSpotId, highlightColor }) {
  const isHighlight = spot.id === highlightSpotId;
  return (
    <Box
      title={`Spot ${spot.id}`}
      sx={{
        width:        10,
        height:       10,
        borderRadius: '50%',
        bgcolor: isHighlight ? highlightColor : '#cbd5e1',
        flexShrink: 0,
      }}
    />
  );
}

// Renders a mini stadium dot-grid that mirrors the real TeleportMap layout:
//   top row  → mapSpots[6..15]  (10 spots across)
//   left col → mapSpots[0..5] reversed  (6 spots down the left edge)
//   right col→ mapSpots[16..21]          (6 spots down the right edge)
// The middle section uses space-between so left/right columns hug the edges
// of the top row width — matching the actual page proportions.
function MiniStadium({ spots, highlightSpotId, highlightColor, noSpotLabel }) {
  if (!spots || spots.length === 0) return null;

  const topSpots   = spots.slice(6, 16);
  const leftSpots  = spots.slice(0, 6).slice().reverse();
  const rightSpots = spots.slice(16, 22);

  const DOT_GAP  = 4;
  // All dots are 10px. rowWidth = 10 dots × 10 + 9 gaps = 136px.
  // Pinning the middle section to this same width keeps left/right columns
  // flush with the first and last dots of the top row — matching the real map.
  const rowWidth = topSpots.length * 10 + (topSpots.length - 1) * DOT_GAP;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${DOT_GAP}px` }}>
      {/* Top row — 10 spots across */}
      <Box sx={{ display: 'flex', gap: `${DOT_GAP}px` }}>
        {topSpots.map(s => (
          <Dot key={s.id} spot={s} highlightSpotId={highlightSpotId} highlightColor={highlightColor} />
        ))}
      </Box>

      {/* Middle section — left and right columns at edges, empty field in between */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: rowWidth,
          my: '2px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${DOT_GAP}px` }}>
          {leftSpots.map(s => (
            <Dot key={s.id} spot={s} highlightSpotId={highlightSpotId} highlightColor={highlightColor} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${DOT_GAP}px` }}>
          {rightSpots.map(s => (
            <Dot key={s.id} spot={s} highlightSpotId={highlightSpotId} highlightColor={highlightColor} />
          ))}
        </Box>
      </Box>

      {/* Spot label */}
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          fontSize: 10.5,
          fontWeight: 700,
          color:      highlightSpotId ? highlightColor : '#94a3b8',
          letterSpacing: 0.3,
        }}
      >
        {highlightSpotId ? `Spot ${highlightSpotId}` : noSpotLabel}
      </Typography>
    </Box>
  );
}

// Spans all 3 grid columns and renders current vs proposed placement side by side,
// aligned with the column headers above (140px label | 1fr current | 1fr proposed).
export default function TeleportMapComparisonRow({ currentSpotId, proposedSpotId }) {
  const [spots,   setSpots]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMapSpots()
      .then(res => {
        const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setSpots(arr);
      })
      .catch(() => setSpots([]))
      .finally(() => setLoading(false));
  }, []);

  const changed = currentSpotId !== proposedSpotId;

  if (loading) {
    return (
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    // Internal grid mirrors the parent ContentComparisonView grid columns exactly
    <Box
      sx={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: { xs: '80px 1fr 1fr', sm: '140px 1fr 1fr' },
        borderTop: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      {/* Label cell — matches the label column above */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          bgcolor: changed ? '#fffbeb' : 'transparent',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', fontSize: 11, lineHeight: 1.4 }}>
          Map Placement
        </Typography>
      </Box>

      {/* Current (live) cell */}
      <Box
        sx={{
          px: 2,
          py: 2.5,
          borderLeft: '1px solid #e2e8f0',
          bgcolor: changed ? '#fff3e0' : 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <MiniStadium
          spots={spots}
          highlightSpotId={currentSpotId ?? null}
          highlightColor="#b45309"
          noSpotLabel="— Not placed —"
        />
      </Box>

      {/* Proposed cell */}
      <Box
        sx={{
          px: 2,
          py: 2.5,
          borderLeft: '1px solid #e2e8f0',
          bgcolor: changed ? '#e8f5e9' : 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <MiniStadium
          spots={spots}
          highlightSpotId={proposedSpotId ?? null}
          highlightColor="#15803d"
          noSpotLabel="— Removed from map —"
        />
      </Box>
    </Box>
  );
}
